import React from 'react';
import clsx from 'clsx';
import { motion, type Easing } from 'motion/react';
import styles from './Progress.module.css';

type ProgressType = 'line' | 'circle' | 'dashboard';
type ProgressStatus = 'normal' | 'active' | 'success' | 'exception';
type ProgressSize = 'sm' | 'md' | 'lg';


type ProgressEase = Easing | Easing[];

interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Progress type */
  type?: ProgressType;
  /** Completion percentage (0–100) */
  percent?: number;
  /** Status determines color */
  status?: ProgressStatus;
  /** Custom fill color */
  strokeColor?: string;
  /** Custom track color */
  trailColor?: string;
  /** Size preset */
  size?: ProgressSize;
  /** Show percentage text */
  showInfo?: boolean;
  /** Stepped progress (line only) */
  steps?: number;
  /** Circle/dashboard width */
  width?: number;
  /** Stroke width for circle/dashboard */
  strokeWidth?: number;
  /**
   * Width-animation duration in seconds for the line variant. Default 0.3s
   * (snappy, suits known percentages). Pass a larger value for a "crawl"
   * effect when the percentage represents an estimated stage target rather
   * than a measured value.
   */
  transitionDuration?: number;
  /**
   * Width-animation easing for the line variant. Default cubic-bezier
   * `[0.4, 0, 0.2, 1]`. Use `'linear'` for an even crawl.
   */
  transitionEase?: ProgressEase;
}

const statusColors: Record<ProgressStatus, string> = {
  normal: 'var(--primary)',
  active: 'var(--primary)',
  success: 'var(--success)',
  exception: 'var(--error)',
};

const trackSizes: Record<ProgressSize, number> = { sm: 4, md: 8, lg: 12 };

function autoStatus(percent: number, status?: ProgressStatus): ProgressStatus {
  if (status) return status;
  return percent >= 100 ? 'success' : 'normal';
}

/**
 * Progress — visual indicator of completion.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | type | 'line' \| 'circle' \| 'dashboard' | 'line' | Shape |
 * | percent | number | 0 | Completion (0–100) |
 * | status | 'normal' \| 'active' \| 'success' \| 'exception' | auto | Color status |
 * | strokeColor | string | — | Custom fill color |
 * | trailColor | string | — | Custom track color |
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Track thickness |
 * | showInfo | boolean | true | Show percentage label |
 * | steps | number | — | Stepped segments (line only) |
 * | width | number | 120 | Circle/dashboard diameter |
 * | strokeWidth | number | 6 | Circle stroke width |
 *
 * @example
 * <Progress percent={30} />
 * <Progress percent={70} status="active" />
 * <Progress percent={100} />
 * <Progress type="circle" percent={75} />
 */
export function Progress({
  type = 'line',
  percent = 0,
  status: statusProp,
  strokeColor,
  trailColor,
  size = 'md',
  showInfo = true,
  steps,
  width: circleWidth = 120,
  strokeWidth: circleStrokeWidth = 6,
  transitionDuration = 0.3,
  transitionEase = [0.4, 0, 0.2, 1],
  style,
  className,
  ...props
}: ProgressProps) {
  const clamped = Math.min(100, Math.max(0, percent));
  const computedStatus = autoStatus(clamped, statusProp);
  const color = strokeColor ?? statusColors[computedStatus];
  const trail = trailColor ?? 'var(--surface-container-high)';
  const trackH = trackSizes[size];

  // ── Circle / Dashboard ──────────────────────────────────────────────────
  if (type === 'circle' || type === 'dashboard') {
    const r = (circleWidth - circleStrokeWidth) / 2;
    const dashAngle = type === 'dashboard' ? 0.75 : 1;
    const circumference = 2 * Math.PI * r * dashAngle;
    const offset = circumference * (1 - clamped / 100);
    const rotation = type === 'dashboard' ? 135 : -90;

    return (
      <div
        className={clsx(styles.circleWrapper, className)}
        style={style}
        {...props}
      >
        <div
          className={styles.circleInner}
          style={{ '--circle-size': `${circleWidth}px`, '--circle-rotation': `${rotation}deg` } as React.CSSProperties}
        >
          <svg width={circleWidth} height={circleWidth}>
            <circle
              cx={circleWidth / 2}
              cy={circleWidth / 2}
              r={r}
              fill="none"
              stroke={trail}
              strokeWidth={circleStrokeWidth}
              strokeDasharray={`${circumference} ${2 * Math.PI * r - circumference}`}
              strokeLinecap="round"
            />
            <circle
              cx={circleWidth / 2}
              cy={circleWidth / 2}
              r={r}
              fill="none"
              stroke={color}
              strokeWidth={circleStrokeWidth}
              strokeDasharray={`${circumference} ${2 * Math.PI * r}`}
              strokeDashoffset={offset}
              strokeLinecap="round"
            />
          </svg>
          {showInfo && (
            <div
              className={clsx(
                styles.circleInfo,
                circleWidth > 80 ? styles.circleFontLg : styles.circleFontSm,
              )}
            >
              {clamped}%
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Stepped Line ────────────────────────────────────────────────────────
  if (steps) {
    const filledSteps = Math.round((clamped / 100) * steps);
    return (
      <div
        className={clsx(styles.stepsWrapper, className)}
        style={style}
        {...props}
      >
        <div className={styles.stepsTrack}>
          {Array.from({ length: steps }, (_, i) => (
            <div
              key={i}
              className={clsx(styles.step, i < filledSteps ? styles.stepFilled : styles.stepEmpty)}
              style={{
                '--step-fill-color': i < filledSteps ? color : trail,
              } as React.CSSProperties}
            />
          ))}
        </div>
        {showInfo && (
          <span className={styles.stepsInfo}>
            {clamped}%
          </span>
        )}
      </div>
    );
  }

  // ── Line ────────────────────────────────────────────────────────────────
  return (
    <div
      className={clsx(styles.lineWrapper, className)}
      style={style}
      {...props}
    >
      <div
        className={styles.track}
        style={{
          '--progress-track-h': `${trackH}px`,
          '--progress-trail': trail,
        } as React.CSSProperties}
      >
        <motion.div
          animate={{ width: `${clamped}%` }}
          transition={{ duration: transitionDuration, ease: transitionEase }}
          className={styles.bar}
          style={{
            '--progress-fill': `${clamped}%`,
            '--progress-color': color,
          } as React.CSSProperties}
        >
          {computedStatus === 'active' && (
            <div className={styles.activeShimmer} />
          )}
        </motion.div>
      </div>
      {showInfo && (
        <span
          className={styles.infoLabel}
          style={{ '--progress-info-color': color } as React.CSSProperties}
        >
          {clamped}%
        </span>
      )}
    </div>
  );
}
